import { useIsTranslationDefined } from '@b-stock/fe-core/bstock-next/hooks'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import {
  CategoryDtoCategoriesEnum,
  type LotDto,
} from '@b-stock/listing-api-client'

type PartialLot = Pick<LotDto, 'categories' | 'overrides'>

const ShortenedCategory = ({
  category,
}: {
  category: CategoryDtoCategoriesEnum
}) => {
  const messageId = useIsTranslationDefined(
    `Listings.Enums.category.short.${category}`
  )

  if (messageId) return <FormattedMessage id={messageId} />

  return <ListingEnums.Subcategory.Component value={category} />
}

export const getShortLotCategories = (lot?: PartialLot) => {
  const categories = getLotCategories(lot)
  return categories.map((x) => <ShortenedCategory category={x} key={x} />)
}

export const getLotCategories = (
  lot?: PartialLot
): CategoryDtoCategoriesEnum[] => {
  const categoryDtos = lot?.overrides?.categories?.length
    ? lot.overrides.categories
    : lot?.categories

  return categoryDtos?.flatMap(({ categories }) => categories ?? []) ?? []
}

/**
 * Gets the "top" category for a lot.
 *
 * Note: Relies on the assumption that the lot category data is already
 * sorted so that the first listed category is the "Top" category.
 */
export const getTopLotCategory = (lot?: PartialLot) => {
  return getLotCategories(lot).at(0)
}

const MOBILE_DEVICE_CATEGORIES: ReadonlySet<CategoryDtoCategoriesEnum> =
  new Set([
    CategoryDtoCategoriesEnum.CellPhones,
    CategoryDtoCategoriesEnum.MixedCellPhones,
  ])

/**
 * A lot of mobile devices: its top category is a cell-phone category, or its
 * manifest carries mobile-device attributes (grades, lock statuses, etc).
 */
export const isMobileDeviceLot = (
  lot: PartialLot & Pick<LotDto, 'mobileDevice'>
) => {
  const topCategory = getTopLotCategory(lot)
  return (
    (!!topCategory && MOBILE_DEVICE_CATEGORIES.has(topCategory)) ||
    Object.values(lot.mobileDevice ?? {}).some(
      (rollup: unknown) => Array.isArray(rollup) && rollup.length > 0
    )
  )
}
